import { c as _c } from "react/compiler-runtime";
/**
 * @file UsersTable.tsx
 * @description
 * -----------------------------------------------------
 * Componente de tabla de usuarios con columnas,
 * paginación, ordenamiento y acciones de selección múltiple
 * -----------------------------------------------------
 * @version 0.0.1a
 * @created 2025-12-11
 * @author DLTCode
 * @copyright © DLTCode
 * @license Private
 * */

import TableComponent from "@/components/ui/tables/TableComponent";
import { BaseFilters } from "@/enums/baseFilters.enum";
import usePagination from "@/hooks/usePagination";
import useSorting from "@/hooks/useSorting";
import { TableComponentProps } from "@/types/ui";
import { ColumnsAllUsers } from "@/types/commons/columns";
import { ColumnDef } from "@tanstack/react-table";
import { useTranslations } from "next-intl";
import { useCallback, useMemo } from "react";
import UsersActions from "./users-actions";
import { useUsersView } from "../users-context";
import BadgeComponent, { BadgeVariant } from "@/components/ui/BadgeComponent";
import { useFormatDateTimeCallback } from "@/hooks/useFormatDate";
const UsersTable: React.FC = () => {
  const $ = _c(46);
  const [pagination, onPaginationChange] = usePagination(BaseFilters.USERS);
  const [sorting, onSortingChange] = useSorting(BaseFilters.USERS);
  const formatDateTime = useFormatDateTimeCallback();
  const {
    data,
    tablePage
  } = useUsersView();
  const tableTrans = useTranslations("TableComponent.TableHeads");
  const options = useTranslations("SelectComponent.Options");
  const labels = useTranslations("Labels");
  let t0;
  if ($[0] !== tableTrans) {
    t0 = {
      accessorKey: "email",
      header: () => tableTrans("mail")
    };
    $[0] = tableTrans;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== tableTrans) {
    t1 = {
      accessorKey: "name",
      header: () => tableTrans("name")
    };
    $[2] = tableTrans;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== tableTrans) {
    t2 = () => tableTrans("role");
    $[4] = tableTrans;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== options) {
    t3 = t4 => {
      const {
        getValue
      } = t4;
      const role = getValue();
      return options(role as Parameters<typeof options>[0]);
    };
    $[6] = options;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t2 || $[9] !== t3) {
    t4 = {
      accessorKey: "role",
      header: t2,
      cell: t3
    };
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== tableTrans) {
    t5 = () => tableTrans("registerDateAndTime");
    $[11] = tableTrans;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== formatDateTime) {
    t6 = t7 => {
      const {
        getValue: getValue_0
      } = t7;
      const createdAt = getValue_0();
      return formatDateTime(createdAt);
    };
    $[13] = formatDateTime;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t5 || $[16] !== t6) {
    t7 = {
      accessorKey: "createdAt",
      header: t5,
      cell: t6
    };
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== tableTrans) {
    t8 = () => tableTrans("status");
    $[18] = tableTrans;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== labels) {
    t9 = t10 => {
      const {
        getValue: getValue_1
      } = t10;
      const status = getValue_1();
      let variant = "gray" as BadgeVariant;
      bb18: switch (status) {
        case "active":
          {
            variant = "green";
            break bb18;
          }
        case "blocked":
          {
            variant = "purple";
            break bb18;
          }
        case "pending":
          {
            variant = "orange";
            break bb18;
          }
        case "eliminated":
          {
            variant = "red";
          }
      }
      return <BadgeComponent variant={variant}>{labels(status as Parameters<typeof labels>[0])}</BadgeComponent>;
    };
    $[20] = labels;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t8 || $[23] !== t9) {
    t10 = {
      accessorKey: "status",
      header: t8,
      cell: t9
    };
    $[22] = t8;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t0 || $[26] !== t1 || $[27] !== t10 || $[28] !== t4 || $[29] !== t7) {
    t11 = [t0, t1, t4, t7, t10];
    $[25] = t0;
    $[26] = t1;
    $[27] = t10;
    $[28] = t4;
    $[29] = t7;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  const columns = t11;
  const selectedRowActions = _temp;
  let t12;
  if ($[31] !== data?.list) {
    t12 = data?.list ?? [];
    $[31] = data?.list;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  const t13 = data?.totalPages ?? 1;
  const t14 = data?.total ?? 0;
  const t15 = data?.page ?? 1;
  let t16;
  if ($[33] !== t13 || $[34] !== t14 || $[35] !== t15) {
    t16 = {
      totalPages: t13,
      total: t14,
      currentPage: t15
    };
    $[33] = t13;
    $[34] = t14;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== columns || $[38] !== onPaginationChange || $[39] !== onSortingChange || $[40] !== pagination || $[41] !== sorting || $[42] !== t12 || $[43] !== t16 || $[44] !== tablePage) {
    t17 = <TableComponent columns={columns} data={t12} page={tablePage} totalData={t16} pagination={pagination} onPaginationChange={onPaginationChange} sorting={sorting} onSortingChange={onSortingChange} selectedRowActions={selectedRowActions} />;
    $[37] = columns;
    $[38] = onPaginationChange;
    $[39] = onSortingChange;
    $[40] = pagination;
    $[41] = sorting;
    $[42] = t12;
    $[43] = t16;
    $[44] = tablePage;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  return t17;
};
export default UsersTable;
function _temp(selectedUsers, onClear) {
  return <UsersActions selectedUsers={selectedUsers} onClear={onClear} />;
}